import React, { useRef, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { apiClient } from "../../App";
import {
  Upload, Download, Loader2, FileUp, CheckCircle, XCircle, Clock,
} from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Progress } from "../ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

export function BulkUploadDialog({ open, onOpenChange, contracts, onUploaded }) {
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bgTask, setBgTask] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [selectedContractForDoc, setSelectedContractForDoc] = useState("");
  const csvInputRef = useRef(null);
  const docInputRef = useRef(null);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => { return stopPolling; }, [stopPolling]);

  const pollTask = useCallback((taskId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get(`/contracts/bulk-upload/${taskId}`);
        const task = res.data;
        setBgTask(task);
        if (task.status === "completed" || task.status === "failed") {
          stopPolling();
          setBulkUploading(false);
          if (task.status === "completed" && task.success_count > 0) {
            toast.success(`Imported ${task.success_count} contracts`);
            onUploaded();
          }
          if (task.error_count > 0) {
            toast.error(`${task.error_count} rows had errors`);
          }
          setBulkResult({
            imported: task.success_count,
            failed: task.error_count,
            errors: task.errors || [],
          });
        }
      } catch {
        stopPolling();
        setBulkUploading(false);
      }
    }, 2000);
  }, [stopPolling, onUploaded]);

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get("/contracts/bulk-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "contrack_bulk_import_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch {
      toast.error("Failed to download template");
    }
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      toast.error("Please upload a CSV or Excel (.xlsx) file");
      return;
    }
    setBulkUploading(true);
    setBulkResult(null);
    setBgTask(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiClient.post("/contracts/bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.task_id) {
        toast.info(`Processing ${response.data.total_rows} rows in background...`);
        setBgTask({ status: "processing", total_rows: response.data.total_rows, processed_rows: 0, success_count: 0 });
        pollTask(response.data.task_id);
      } else {
        setBulkResult(response.data);
        setBulkUploading(false);
        if (response.data.imported > 0) {
          toast.success(`Imported ${response.data.imported} contracts`);
          onUploaded();
        }
        if (response.data.failed > 0) toast.error(`${response.data.failed} rows failed`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk upload failed");
      setBulkUploading(false);
    } finally {
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  const handleDocUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!selectedContractForDoc) { toast.error("Please select a contract first"); return; }
    setDocUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post(`/contracts/${selectedContractForDoc}/upload-document`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onUploaded();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setDocUploading(false);
      if (docInputRef.current) docInputRef.current.value = "";
    }
  };

  const progressPct = bgTask && bgTask.total_rows > 0
    ? Math.round((bgTask.processed_rows / bgTask.total_rows) * 100)
    : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setBulkResult(null); setBgTask(null); stopPolling(); } onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk Upload Contracts</DialogTitle>
          <DialogDescription>Import contracts via CSV or Excel (.xlsx) and attach signed documents</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          <div className="p-4 border rounded-sm space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-medium">1</div>
              <div><h4 className="font-medium">Download Template</h4><p className="text-sm text-slate-500">Get the CSV template with all required columns</p></div>
            </div>
            <Button onClick={handleDownloadTemplate} variant="outline" className="gap-2 rounded-sm ml-11" data-testid="download-csv-template-btn"><Download size={16} />Download Template</Button>
          </div>
          <div className="p-4 border rounded-sm space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <h4 className="font-medium">Upload CSV or Excel</h4>
                <p className="text-sm text-slate-500">Upload completed CSV or .xlsx file (max 50MB). Files over 100 rows are processed in the background.</p>
              </div>
            </div>
            <div className="ml-11">
              <input ref={csvInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleBulkUpload} className="hidden" data-testid="bulk-file-input" />
              <Button onClick={() => csvInputRef.current?.click()} disabled={bulkUploading} variant="outline" className="gap-2 rounded-sm" data-testid="upload-csv-btn">
                {bulkUploading ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />}
                {bulkUploading ? "Processing..." : "Upload File"}
              </Button>
            </div>

            {/* Background task progress */}
            {bgTask && bgTask.status === "processing" && (
              <div className="ml-11 mt-3 space-y-2" data-testid="bulk-progress">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock size={14} className="text-cyan-600 animate-pulse" />
                  Processing {bgTask.processed_rows || 0} of {bgTask.total_rows} rows...
                </div>
                <Progress value={progressPct} className="h-2" />
                <p className="text-xs text-slate-400">{progressPct}% complete{bgTask.success_count > 0 ? ` — ${bgTask.success_count} imported so far` : ""}</p>
              </div>
            )}

            {/* Results */}
            {bulkResult && (
              <div className="ml-11 mt-3 space-y-2" data-testid="bulk-result">
                <div className="flex items-center gap-4">
                  {bulkResult.imported > 0 && <div className="flex items-center gap-2 text-emerald-600"><CheckCircle size={16} /><span className="text-sm font-medium">{bulkResult.imported} imported</span></div>}
                  {bulkResult.failed > 0 && <div className="flex items-center gap-2 text-red-600"><XCircle size={16} /><span className="text-sm font-medium">{bulkResult.failed} failed</span></div>}
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 rounded-sm p-3 max-h-32 overflow-y-auto">
                    <p className="text-xs font-medium text-red-700 mb-1">Errors:</p>
                    {bulkResult.errors.map((err, i) => (
                      <p key={`err-${i}`} className="text-xs text-red-600">
                        {typeof err === "string" ? err : `Row ${err.row}: ${err.error}`}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="p-4 border rounded-sm space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-medium">3</div>
              <div><h4 className="font-medium">Attach Signed Documents (Optional)</h4><p className="text-sm text-slate-500">Upload PDF/Word copies of signed contracts and link them</p></div>
            </div>
            <div className="ml-11 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Select Contract</Label>
                <Select value={selectedContractForDoc} onValueChange={setSelectedContractForDoc}>
                  <SelectTrigger className="rounded-sm" data-testid="select-contract-for-doc"><SelectValue placeholder="Select a contract" /></SelectTrigger>
                  <SelectContent>{contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleDocUpload} className="hidden" />
                <Button onClick={() => docInputRef.current?.click()} disabled={docUploading || !selectedContractForDoc} variant="outline" className="gap-2 rounded-sm" data-testid="upload-doc-btn">
                  {docUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {docUploading ? "Uploading..." : "Upload Document"}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4"><Button onClick={() => onOpenChange(false)} className="rounded-sm">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
